import { useState, useEffect } from 'react';
import { Dimensions, PixelRatio, Platform } from 'react-native';

const { width: RAW_WIDTH, height: RAW_HEIGHT } = Dimensions.get('window');

/** On web, clamp dimensions for scaling math so elements stay phone-sized on large monitors. */
/** On web and native tablets, clamp dimensions for scaling math so elements stay phone-sized. */
const SCALE_CAP = 1024;
const shouldCap = (Platform.OS === 'web' || RAW_WIDTH >= 768) && RAW_WIDTH > SCALE_CAP;
const SCREEN_WIDTH = shouldCap ? SCALE_CAP : RAW_WIDTH;
const SCREEN_HEIGHT = shouldCap ? RAW_HEIGHT * (SCALE_CAP / RAW_WIDTH) : RAW_HEIGHT;

/** Design baseline: iPhone SE (375 x 667). */
const BASE_WIDTH = 375;
const BASE_HEIGHT = 667;

/** Scale factor based on screen width relative to baseline. */
const widthScale = SCREEN_WIDTH / BASE_WIDTH;
const heightScale = SCREEN_HEIGHT / BASE_HEIGHT;

/**
 * Moderate scale - averages width scale with 1 to prevent extremes on tablets.
 * @param size - base size in points
 * @param factor - dampening factor (0 = no scaling, 1 = full scaling)
 */
const moderateScale = (size: number, factor = 0.5): number => {
  return size + (widthScale - 1) * size * factor;
};

// Web treats phone-spec sizes as canonical and never grows them; layout-level
// max-widths handle desktop. Native keeps proportional scaling so small phones
// and tablets dampen toward the design baseline.
const IS_WEB = Platform.OS === 'web';

export const rs = (size: number): number => {
  if (IS_WEB) return size;
  return Math.round(PixelRatio.roundToNearestPixel(moderateScale(size, 0.4)));
};

export const fs = (size: number): number => {
  if (IS_WEB) return size;
  const scaled = moderateScale(size, 0.35);
  const maxScaled = size * 1.3;
  return Math.round(PixelRatio.roundToNearestPixel(Math.min(scaled, maxScaled)));
};

export const vs = (size: number): number => {
  if (IS_WEB) return size;
  const scale = size + (heightScale - 1) * size * 0.4;
  return Math.round(PixelRatio.roundToNearestPixel(scale));
};

export const iconSize = (base: number): number => (IS_WEB ? base : rs(base));

/** Initial screen width at module load time (raw, unclamped). */
export const screenWidth = RAW_WIDTH;

/** Initial screen height at module load time (raw, unclamped). */
export const screenHeight = RAW_HEIGHT;

/** Whether initial screen width is smaller than iPhone SE baseline. */
export const isSmallScreen = SCREEN_WIDTH < 375;

/** Whether initial screen width qualifies as tablet-sized. */
export const isTablet = SCREEN_WIDTH >= 768;

/**
 * useResponsive - reactive hook returning current window dimensions and breakpoint flags.
 * Re-renders on window resize (web) or orientation change (native).
 */
export function useResponsive(): {
  readonly width: number;
  readonly height: number;
  readonly isSmallScreen: boolean;
  readonly isTablet: boolean;
  readonly isDesktopWeb: boolean;
} {
  const [dims, setDims] = useState(() => Dimensions.get('window'));

  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => setDims(window));
    return () => sub.remove();
  }, []);

  return {
    width: dims.width,
    height: dims.height,
    isSmallScreen: dims.width < 375,
    isTablet: dims.width >= 768,
    isDesktopWeb: Platform.OS === 'web' && dims.width >= 1024,
  };
}
